import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import { CobaltTriangle, OrangeArc, StripedTriangle } from "../components/Motifs";
import { getEmail, getMailto, SCHEDULE_URL } from "../lib/site";
import { ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, name, type = "text", required = true, as = "input", value, onChange, placeholder }) => {
  const inputProps = {
    id: name,
    name,
    value,
    onChange,
    required,
    placeholder,
    "data-testid": `field-${name}`,
    className:
      "w-full bg-transparent border-b border-ink/30 focus:border-cobalt outline-none py-3 text-ink placeholder:text-ink/40 text-base transition-colors",
  };
  return (
    <div className="relative">
      <label htmlFor={name} className="overline block mb-2 text-ink-soft">
        {label} {required && <span className="text-orange-brand">*</span>}
      </label>
      {as === "textarea" ? (
        <textarea rows={5} {...inputProps} className={`${inputProps.className} resize-none`} />
      ) : (
        <input type={type} {...inputProps} />
      )}
    </div>
  );
};

const Collaborate = () => {
  const [form, setForm] = useState({ name: "", organization: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const [email, setEmailAddr] = useState("");
  const [mailto, setMailto] = useState("#");

  React.useEffect(() => {
    setEmailAddr(getEmail());
    setMailto(getMailto());
  }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Thanks — we'll be in touch soon.");
      setForm({ name: "", organization: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitNewsletter = async (e) => {
    e.preventDefault();
    setNewsletterSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/newsletter`, { email: newsletterEmail });
      toast.success(data.message || "Subscribed.");
      setNewsletterEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Please enter a valid email.");
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  return (
    <div data-testid="collaborate-page">
      {/* Hero + form */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-24 grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal>
              <div className="overline">Collaborate</div>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-ink">
                Let's <em className="italic text-cobalt">explore</em>.
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-lg text-ink-soft leading-relaxed">
                Whether you're launching a statewide initiative, planning a leadership convening, designing a new
                strategy, or simply looking for a thought partner — I'd love to hear what you're building.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-10 space-y-6">
                <div>
                  <div className="overline text-ink">Email</div>
                  <a
                    href={mailto}
                    data-testid="collaborate-email-link"
                    className="mt-2 inline-flex items-center gap-2 font-display text-2xl lg:text-3xl text-cobalt hover:text-orange-brand transition-colors"
                  >
                    Send Leah an email
                    <ArrowUpRight size={20} />
                  </a>
                </div>
                <div>
                  <div className="overline text-ink">Call, text, or WhatsApp</div>
                  <a
                    href="tel:+19172916666"
                    data-testid="collaborate-phone-link"
                    className="mt-2 inline-block font-display text-2xl lg:text-3xl text-cobalt hover:text-orange-brand transition-colors"
                  >
                    917.291.6666
                  </a>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs tracking-[0.15em] uppercase font-medium">
                    <a
                      href="tel:+19172916666"
                      data-testid="collaborate-call-link"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-ink/30 text-ink hover:border-cobalt hover:text-cobalt transition-colors"
                    >
                      Call
                    </a>
                    <a
                      href="sms:+19172916666"
                      data-testid="collaborate-text-link"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-ink/30 text-ink hover:border-cobalt hover:text-cobalt transition-colors"
                    >
                      Text
                    </a>
                    <a
                      href="https://wa.me/19172916666"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="collaborate-whatsapp-link"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-ink/30 text-ink hover:border-cobalt hover:text-cobalt transition-colors"
                    >
                      WhatsApp <ArrowUpRight size={12} />
                    </a>
                  </div>
                </div>
                <div>
                  <div className="overline text-ink">Schedule a conversation</div>
                  <a
                    href={SCHEDULE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="collaborate-schedule"
                    className="mt-2 inline-flex items-center gap-2 font-display text-xl lg:text-2xl text-cobalt hover:text-orange-brand transition-colors"
                  >
                    Book a time on Leah's calendar
                    <ArrowUpRight size={18} />
                  </a>
                </div>
                <div className="pt-4 border-t border-tan/70">
                  <div className="overline text-ink">Based in</div>
                  <p className="mt-2 text-ink-soft leading-relaxed" data-testid="collaborate-location">
                    Elenem Strategies is <span className="text-ink font-medium">New York City based</span>, with
                    the ability to work remotely across time zones and travel as needed for effective
                    collaboration.
                  </p>
                </div>
              </div>
            </Reveal>

            {/* Decorative arrangement */}
            <div className="relative mt-16 h-40 hidden lg:block">
              <StripedTriangle className="absolute top-0 left-0" size={140} />
              <CobaltTriangle className="absolute bottom-0 right-8" size={100} flip />
              <OrangeArc className="absolute top-4 right-0" size={90} />
            </div>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <form
                onSubmit={submit}
                className="bg-cream border border-tan p-8 lg:p-12 space-y-8"
                data-testid="contact-form"
              >
                <div>
                  <div className="overline">Start a conversation</div>
                  <h2 className="mt-2 font-display text-3xl lg:text-4xl text-ink leading-tight">
                    Tell me about what you're building.
                  </h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-8">
                  <Field label="Your name" name="name" value={form.name} onChange={onChange} placeholder="Alex Rivera" />
                  <Field
                    label="Organization"
                    name="organization"
                    required={false}
                    value={form.organization}
                    onChange={onChange}
                    placeholder="Optional"
                  />
                </div>
                <Field
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="you@organization.org"
                />
                <Field
                  label="What are you exploring?"
                  name="message"
                  as="textarea"
                  value={form.message}
                  onChange={onChange}
                  placeholder="A few sentences on the challenge, timing, or the idea you'd like to talk through."
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-cobalt text-cream px-10 py-4 text-sm tracking-[0.15em] uppercase font-medium hover:bg-cobalt-deep transition-colors disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send message"}
                  <ArrowUpRight size={16} />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="relative py-24 bg-ink text-cream overflow-hidden">
        <OrangeArc className="absolute -top-10 -right-10" size={180} />
        <StripedTriangle className="absolute -bottom-10 -left-10 opacity-20" size={220} />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <Reveal>
            <div className="overline text-orange-brand">Field notes</div>
            <h2 className="mt-4 font-display text-4xl lg:text-5xl leading-tight">
              Explorations, delivered occasionally.
            </h2>
            <p className="mt-6 text-cream/80 max-w-2xl mx-auto text-lg">
              A short letter with lessons from the field, new frameworks, and a few unexpected ideas — sent when
              there's something genuinely worth sharing.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <form
              onSubmit={submitNewsletter}
              className="mt-10 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto"
              data-testid="newsletter-form"
            >
              <input
                type="email"
                required
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="newsletter-email"
                className="flex-1 bg-transparent border border-cream/40 focus:border-orange-brand outline-none px-5 py-3 text-cream placeholder:text-cream/40 transition-colors"
              />
              <button
                type="submit"
                disabled={newsletterSubmitting}
                data-testid="newsletter-submit"
                className="bg-orange-brand text-cream px-8 py-3 text-sm tracking-[0.15em] uppercase font-medium hover:bg-orange-soft transition-colors disabled:opacity-60"
              >
                {newsletterSubmitting ? "…" : "Subscribe"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Collaborate;
